import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import api, { setForceLogout } from '../utils/api';
import {
  getAccessToken, setTokens, clearTokens, decodeJwt,
} from '../utils/tokens';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser]       = useState(null);
  const [token, setToken]     = useState(() => getAccessToken());
  const [loading, setLoading] = useState(true);

  // Stable logout — invoked by the api interceptor when refresh fails or the
  // account is gone (deleted user, etc).
  const logout = useCallback(() => {
    clearTokens();
    setToken(null);
    setUser(null);
  }, []);

  // Register logout with the api interceptor so it can clear React state too.
  useEffect(() => {
    setForceLogout(logout);
  }, [logout]);

  // On mount: hydrate from stored token + verify the user still exists.
  // The api interceptor will silently refresh expired access tokens, so we
  // no longer need to bail out just because the access token is past its
  // expiry — the next request will refresh it.
  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }

    const payload = decodeJwt(token);
    if (payload) setUser(payload);
    setLoading(false);

    // Hit /users/:id to confirm account exists + refresh profile fields.
    // Errors are handled by the api interceptor (refresh → retry, or logout).
    const id = payload?.id || payload?._id || payload?.userId;
    if (id) {
      api.get(`/users/${id}`)
        .then((res) => {
          const fresh = res.data?.user || res.data?.data || res.data;
          if (fresh && typeof fresh === 'object') {
            setUser((prev) => ({ ...prev, ...fresh }));
          }
        })
        .catch(() => {});
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const login = async (username, password) => {
    // Bare axios — login is the one call that mustn't go through the
    // refresh interceptor (we have no tokens yet).
    const res = await axios.post(
      `${process.env.REACT_APP_API_URL}/auth/login`,
      { username, password },
      { timeout: 60_000 },
    );

    const accessToken  = res.data.accessToken || res.data.token;
    const refreshToken = res.data.refreshToken;

    if (!accessToken) throw new Error('Login response missing access token');

    setTokens({ accessToken, refreshToken });
    setToken(accessToken);

    const jwtPayload = decodeJwt(accessToken) || {};
    const apiUser    = res.data.user || res.data.data || {};
    // Merge JWT claims under API response so role/id are always present.
    const userData = { ...jwtPayload, ...apiUser };
    setUser(userData);
    return userData;
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
